import React, { useState, useEffect } from 'react';
import { Users, Mail, MessageCircle, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { dashboardApi } from '../services/api';

const StatCard = ({ icon: Icon, title, value, change, color }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-500 text-sm font-medium">{title}</p>
        <p className="text-3xl font-bold mt-2">{value}</p>
        {change && (
          <p className={`text-sm mt-2 ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {change > 0 ? '+' : ''}{change}% from last week
          </p>
        )}
      </div>
      <div className={`p-3 rounded-full ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardApi.getStats();
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load stats:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const overview = stats?.overview || {};
  
  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-900 mb-8">Dashboard Overview</h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Users}
          title="Total Influencers"
          value={overview.total_influencers?.toLocaleString() || '0'}
          change={12}
          color="bg-blue-600"
        />
        <StatCard
          icon={Mail}
          title="Contacted"
          value={overview.contacted_count?.toLocaleString() || '0'}
          change={8}
          color="bg-green-600"
        />
        <StatCard
          icon={MessageCircle}
          title="Replied"
          value={overview.replied_count?.toLocaleString() || '0'}
          change={15}
          color="bg-purple-600"
        />
        <StatCard
          icon={TrendingUp}
          title="Partnered"
          value={overview.partnered_count?.toLocaleString() || '0'}
          change={20}
          color="bg-yellow-600"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Daily Discovery */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Daily Discoveries</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats?.daily || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="discovery_date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="influencers_discovered" stroke="#3B82F6" name="Discovered" />
              <Line type="monotone" dataKey="with_email" stroke="#10B981" name="With Email" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Outreach Performance */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Outreach Performance</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats?.outreach || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="outreach_date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="total_sent" stroke="#3B82F6" name="Sent" />
              <Line type="monotone" dataKey="replied_count" stroke="#10B981" name="Replied" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Runs */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Recent Discovery Runs</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Started At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profiles Found
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Qualified
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Emails Sent
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(stats?.runs || []).map((run) => (
                <tr key={run.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(run.run_started_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      run.status === 'completed' ? 'bg-green-100 text-green-800' : 
                      run.status === 'running' ? 'bg-blue-100 text-blue-800' : 
                      'bg-red-100 text-red-800'
                    }`}>
                      {run.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {run.profiles_scraped}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {run.profiles_qualified}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {run.outreach_sent}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
